import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, ArrowRight, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { trackConversion } from "../lib/analytics";
import { useAuthorityProgress } from "../hooks/useAuthorityProgress";
import { AUTHORITY_PHASES, AUTHORITY_ITEM_COUNT } from "../data/authorityChecklist";
import { AuthorityStatusCard } from "../components/authority/AuthorityStatusCard";
import { AuthorityPhase } from "../components/authority/AuthorityPhase";
import { FirstLoadWeek } from "../components/authority/FirstLoadWeek";
import { WinWall } from "../components/authority/WinWall";
import { ScamAlertCard } from "../components/authority/ScamAlertCard";
import { AuthorityShareCard, shareUrl } from "../components/authority/AuthorityShareCard";
import { InsuranceQuoteTracker } from "../components/authority/InsuranceQuoteTracker";
import { CarrierPacketPanel } from "../components/authority/CarrierPacketPanel";
import HomeScreenPrompt from "../components/HomeScreenPrompt";

// Routes: /authority-checklist (public lead magnet) and /app/authority (embedded in AppLayout).
export default function AuthorityChecklistPage({ embedded = false }) {
  const { user } = useAuth();
  const progress = useAuthorityProgress(user);
  const doneCount = Object.keys(progress.state.checked || {}).length;
  // TikTok/Meta ViewContent for the retargeting audience — waits for the async pixel loader.
  useEffect(() => {
    let tries = 0;
    const fire = () => { if (window.ttq || tries >= 4) { trackConversion("checklist_view", { content_name: "authority_checklist" }); return; } tries += 1; setTimeout(fire, 1200); };
    fire();
  }, []);
  const extras = {
    "insurance-quotes": <InsuranceQuoteTracker quotes={progress.state.quotes || []} onChange={progress.setQuotes} renewal={progress.renewal} user={user} />,
    "carrier-packet": <CarrierPacketPanel user={user} onSent={() => progress.markDone("carrier-packet")} />,
  };

  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: "New Authority Checklist", url: shareUrl("native") });
      else { await navigator.clipboard.writeText(shareUrl("copy")); toast.success("Link copied"); }
    } catch { /* user cancelled */ }
  };

  const body = (
    <div className={embedded ? "max-w-4xl" : "max-w-4xl mx-auto px-6"} data-testid="authority-checklist-page">
      <header className={embedded ? "pt-2 pb-8" : "pt-14 pb-10"}>
        <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-4 py-1.5 text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-5">
          <ClipboardList className="w-3.5 h-3.5" /> Free · {AUTHORITY_ITEM_COUNT} steps · Ticks save automatically
        </div>
        <h1 className={`font-black tracking-tight leading-[1.02] mb-4 ${embedded ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl lg:text-6xl"}`}>
          New Authority Checklist.<br />
          <span className="text-[#FF5A1F]">MC# to first load,</span> in order.
        </h1>
        <p className="text-base md:text-lg text-white/65 max-w-2xl leading-relaxed">
          Every filing, permit and account a brand-new carrier needs — sequenced day by day, with what it costs, how long it takes, and the official link. Nothing to sign up for.
        </p>
      </header>

      <AuthorityStatusCard
        state={progress.state} update={progress.update} reset={progress.reset} user={user} synced={progress.synced}
        reminders={progress.reminders} watch={progress.watch} checkNow={progress.checkNow} checking={progress.checking}
      />

      <WinWall />

      <ScamAlertCard />

      <div className="space-y-12 mt-12">
        {AUTHORITY_PHASES.map((phase) => (
          <div key={phase.id} className="space-y-12">
            <AuthorityPhase phase={phase} checked={progress.state.checked} onToggle={progress.toggle} extras={extras} />
            {phase.id === "activation" && (
              <FirstLoadWeek user={user} watch={progress.watch} checked={progress.state.checked} onToggle={progress.toggle} sentAt={progress.firstLoadPlanSentAt} />
            )}
          </div>
        ))}
      </div>

      <AuthorityShareCard user={user} />

      <div className="mt-14 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-2">{user ? "Know someone getting their authority?" : "Keep this across devices"}</div>
          <p className="text-white/70 text-sm leading-relaxed">
            {user
              ? "Send them this checklist — they can tick it off from their phone, no account needed."
              : "Your ticks are saved on this device. Start your 7-day free trial and they follow you to any phone or laptop — plus you get the load board, IFTA miles and invoicing the day you go ACTIVE."}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 shrink-0">
          {!user && (
            <Link to="/signup" className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-bold rounded-full px-5 py-2.5 text-sm transition-colors" data-testid="authority-signup-cta">
              Save my progress — free <ArrowRight className="w-4 h-4" />
            </Link>
          )}
          <button type="button" onClick={share} className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-white/40 hover:bg-white/5 px-5 py-2.5 text-sm font-semibold transition-colors" data-testid="authority-share-btn">
            <Share2 className="w-4 h-4" /> Share checklist
          </button>
        </div>
      </div>
    </div>
  );

  if (embedded) return <div className="text-white pb-12">{body}</div>;

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <div className="border-b border-white/10 bg-neutral-950/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-1" data-testid="authority-home-link">
            <span className="font-black text-xl tracking-tight">Freight</span>
            <span className="font-black text-xl tracking-tight text-[#FF5A1F]">Pilot</span>
            <span className="font-black text-xl tracking-tight">AI</span>
          </Link>
          <div className="flex items-center gap-5 text-sm">
            <Link to="/tools" className="text-white/70 hover:text-white transition-colors" data-testid="authority-tools-link">All free tools</Link>
            <Link to={user ? "/app/authority" : "/login"} className="text-white/70 hover:text-white transition-colors" data-testid="authority-login-link">{user ? "Open in app" : "Log in"}</Link>
          </div>
        </div>
      </div>
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 10%, #FF5A1F 0%, transparent 40%)" }} />
        <div className="relative pb-20">{body}</div>
      </div>
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-white/50">
          <div>© 2026 <b className="text-white">FreightPilotAI</b> · Costs and timelines are typical ranges, not quotes — confirm with FMCSA and your state.</div>
          <div className="flex items-center gap-4">
            <Link to="/broker-check" className="hover:text-white">BrokerCheck</Link>
            <Link to="/rate-check" className="hover:text-white">RateCheck</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </div>
        </div>
      </footer>
      <HomeScreenPrompt source="checklist" when={doneCount > 0} delayMs={1500} />
    </div>
  );
}
